import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { EventTypeDto, DropDownEventTypesStore } from '../../../stores/configuration/events-workflow/event-types-store';
import { nameof } from '../../../utils/object';
import { QueryParameters, ItemReference } from 'stores/dataStore';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';

interface MultiEventTypeEditorProps extends WithTranslation {
  //value: string[];
  value: ItemReference[];
  onChange: (value: string[], items: ItemReference[]) => void;
  placeholder?: string;
  eventTypesStore?: DropDownEventTypesStore;
  nullable?: boolean;
  minWidth?: number;
  reloadOnChange?: boolean;
  filteredEventTypes?: string[];
  id?: string;
  className?: string;
  directOnly?: boolean;
  filterDirectEventCreator?: boolean;
  filterRequestCreator?: boolean;
  practicalTestsOnly?: boolean;
  error?: boolean;
  useOriginalEventTypeIdAsValue?: boolean;
  schedulerRender?: boolean;
  disabled?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

interface MultiEventTypeEditorState {
  value: ItemReference[];
  availableEventTypes: { [id: string]: EventTypeDto };
  eventTypeOptions: { text: string; value: string }[];
  isLoading?: boolean;
}

@connect(['eventTypesStore', DropDownEventTypesStore])
class MultiEventTypeEditor extends React.Component<MultiEventTypeEditorProps, MultiEventTypeEditorState> {
  private get eventTypeStore() {
    return this.props.eventTypesStore;
  }

  constructor(props: MultiEventTypeEditorProps) {
    super(props);
    this.state = { value: this.props.value, availableEventTypes: {}, eventTypeOptions: [], isLoading: false };
  }

  componentDidMount() {
    if (!this.props.loadDataOnOpen) {
      this.load();
    }
  }

  componentDidUpdate(prevProps: MultiEventTypeEditorProps) {
    const prevValue = prevProps.value;
    const currentValue = this.props.value;

    const hasValueChanged =
      (!prevValue && currentValue) ||
      (!currentValue && prevValue) ||
      (Array.isArray(prevValue) &&
        Array.isArray(currentValue) &&
        (prevValue.length !== currentValue.length || !prevValue.every((v, i) => v.id === currentValue[i]?.id)));

    if (hasValueChanged) {
      this.setState(
        {
          value: currentValue || []
        },
        () => {
          if (!currentValue || currentValue.length === 0) {
            this.props.onChange([], []);
          }
        }
      );
    }
  }

  private initEventTypes = async () => {
    try {
      this.setState({ isLoading: true });
      let filter = [];
      if (this.props.filteredEventTypes && this.props.filteredEventTypes.length > 0) {
        if (!!this.props.useOriginalEventTypeIdAsValue) {
          filter.push({ OriginalEventTypeId: { in: { type: 'guid', value: this.props.filteredEventTypes } } });
        } else {
          filter.push({ id: { in: { type: 'guid', value: this.props.filteredEventTypes } } });
        }
      }

      if (this.props.directOnly) {
        filter.push({ DirectEventsAllowed: true });
      }
      if (this.props.practicalTestsOnly) {
        filter.push({ PracticalTest: true });
      }
      filter.push({ Active: true });
      filter.push({ Disabled: false });

      const parameters: QueryParameters =
        !!this.props.filterDirectEventCreator || !!this.props.filterRequestCreator ? ({} as QueryParameters) : undefined;

      if (!!this.props.filterDirectEventCreator) parameters['filterDirectEventCreator'] = 'true';

      if (!!this.props.filterRequestCreator) parameters['filterRequestCreator'] = 'true';

      const key = !!this.props.useOriginalEventTypeIdAsValue ? 'originalEventTypeId' : 'id';

      return await this.eventTypeStore
        .getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<EventTypeDto>('name'), useProfile: false }],
          filter,
          parameters
        })
        .then(eTypes => {
          const dict = {};
          const options = [];

          eTypes.items.forEach(et => {
            dict[et[key]] = et;
            options.push({ text: et.name, value: et[key] });
          });

          this.setState({ availableEventTypes: dict, eventTypeOptions: options });
        });
    } catch (error) {
      console.error('Error loading locations:', error);
      this.setState({ availableEventTypes: {}, eventTypeOptions: [] });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  selectedEventType = (value: any) => {
    const safeValue = value || [];
    this.setState({ value: safeValue });
    const items = (safeValue as string[]).map(x => ({ id: x, title: this.state.availableEventTypes?.[x]?.name }));
    this.props.onChange(safeValue, items);
  };

  load = () => {
    if ((this.state.eventTypeOptions || []).length <= 0) {
      this.initEventTypes();
    }
  };

  onOpenDropDown = () => {
    if (this.props.loadDataOnOpen) {
      this.load();
    }
  };

  public render() {
    const { t, placeholder, className, schedulerRender, disabled } = this.props;
    const { eventTypeOptions, value, isLoading } = this.state;
    if (schedulerRender)
      return (
        <TailwindMultiSelect
          options={
            isLoading
              ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
              : (eventTypeOptions || []).length > 0
              ? (eventTypeOptions || []).map(x => ({ ...x, id: x.value }))
              : [{ id: 'no-options', text: t('No options available'), disabled: true }]
          }
          value={value || []}
          onChange={this.selectedEventType}
          placeholder={placeholder}
          clearable
          disabled={disabled}
          className={className}
          onBlur={e => this.props.onBlur && this.props.onBlur(e)}
          onFocus={e => this.props.onFocus && this.props.onFocus(e)}
          searchable={!isLoading}
          onOpenDropDown={this.onOpenDropDown}
        />
      );

    return <p>Not implemented option</p>;
  }
}

export default withTranslation()(MultiEventTypeEditor);
